/*
Copyright 2023, 2024 New Vector Ltd.
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type MemberActions,
  type Moderation,
  type ModeratorControls,
} from "../FloorControl.ts";
import { ParticipantEvent, Track } from "livekit-client";

import { type ReactionOption } from "../../reactions";
import { type Behavior, computed, fromEvents } from "../Behavior";
import { type LocalUserMediaViewModel } from "./LocalUserMediaViewModel";
import {
  createMemberMedia,
  type MemberMediaInputs,
  type BaseMemberMediaViewModel,
} from "./MemberMediaViewModel";
import { type RemoteUserMediaViewModel } from "./RemoteUserMediaViewModel";
import { type Scope } from "../Scope";
import { showConnectionStats } from "../../settings/settings";
import { rtpStreamStats$, type RtpStreamStats } from "./rtpStreamStats";
import { MEDIA_EVENTS } from "../trackReference";

/**
 * A participant's user media (i.e. their microphone and camera feed).
 */
export type UserMediaViewModel =
  | LocalUserMediaViewModel
  | RemoteUserMediaViewModel;

export interface BaseUserMediaViewModel extends BaseMemberMediaViewModel {
  type: "user";
  speaking$: Behavior<boolean>;
  audioEnabled$: Behavior<boolean>;
  videoEnabled$: Behavior<boolean>;
  videoOrientation$: Behavior<"landscape" | "portrait">;
  /**
   * The expected identity of the LiveKit participant. Exposed for debugging.
   */
  rtcBackendIdentity: string;
  handRaised$: Behavior<Date | null>;
  /**
   * Where this user's raised hand comes in the queue of raised hands (1 for
   * the first), or null if theirs is the only one or it isn't raised.
   */
  handRaisedOrder$: Behavior<number | null>;
  reaction$: Behavior<ReactionOption | null>;
  /** What moderators have asked of this device through floor control. */
  moderation$: Behavior<Moderation>;
  /** Floor control actions on this device, if the local user is a moderator. */
  moderator$: Behavior<ModeratorControls | null>;
  /** Kicking or banning this user, if the local user has the power to. */
  memberActions$: Behavior<MemberActions | null>;
  /** Whether this user has moderator powers (may change floor control). */
  isModerator$: Behavior<boolean>;
  /** Whether this user is pinned to the spotlight. */
  pinned$: Behavior<boolean>;
  audioStreamStats$: Behavior<RtpStreamStats | undefined>;
  videoStreamStats$: Behavior<RtpStreamStats | undefined>;
}

export interface BaseUserMediaInputs extends Omit<
  MemberMediaInputs,
  "audioSource" | "videoSource"
> {
  rtcBackendIdentity: string;
  handRaised$: Behavior<Date | null>;
  handRaisedOrder$: Behavior<number | null>;
  reaction$: Behavior<ReactionOption | null>;
  moderation$: Behavior<Moderation>;
  moderator$: Behavior<ModeratorControls | null>;
  memberActions$: Behavior<MemberActions | null>;
  isModerator$: Behavior<boolean>;
  pinned$: Behavior<boolean>;
  statsType: "inbound-rtp" | "outbound-rtp";
}

export function createBaseUserMedia(
  scope: Scope,
  {
    rtcBackendIdentity,
    handRaised$,
    handRaisedOrder$,
    reaction$,
    moderation$,
    moderator$,
    memberActions$,
    isModerator$,
    pinned$,
    statsType,
    ...inputs
  }: BaseUserMediaInputs,
): BaseUserMediaViewModel {
  const { participant$ } = inputs;
  const trackEnabled$ = (source: Track.Source): Behavior<boolean> =>
    fromEvents(
      scope,
      participant$,
      MEDIA_EVENTS,
      (p) => p?.getTrackPublication(source)?.isMuted === false,
    );
  const streamStats$ = (
    source: Track.Source,
  ): Behavior<RtpStreamStats | undefined> =>
    rtpStreamStats$(
      scope,
      participant$,
      source,
      statsType,
      showConnectionStats.value$,
    );

  const memberMedia = createMemberMedia(scope, {
    ...inputs,
    audioSource: Track.Source.Microphone,
    videoSource: Track.Source.Camera,
  });
  const audioEnabled$ = trackEnabled$(Track.Source.Microphone);
  const videoEnabled$ = trackEnabled$(Track.Source.Camera);

  return {
    ...memberMedia,
    type: "user",
    speaking$: fromEvents(
      scope,
      participant$,
      [ParticipantEvent.IsSpeakingChanged],
      (p) => p?.isSpeaking ?? false,
    ),
    // Audio a moderator has muted is treated as muted whether or not the
    // device has caught up and stopped publishing it yet. (Video mutes are
    // applied by viewers, see RemoteUserMediaViewModel; our own camera is
    // shown to us as it is.)
    audioEnabled$: computed(
      () => !moderation$.value.audioMuted && audioEnabled$.value,
    ),
    videoEnabled$,
    videoOrientation$: computed(() =>
      memberMedia.videoAspectRatio$.value > 1 ? "landscape" : "portrait",
    ),
    rtcBackendIdentity,
    handRaised$,
    handRaisedOrder$,
    reaction$,
    moderation$,
    moderator$,
    memberActions$,
    isModerator$,
    pinned$,
    audioStreamStats$: streamStats$(Track.Source.Microphone),
    videoStreamStats$: streamStats$(Track.Source.Camera),
  };
}
